import React, { useState, useRef, useEffect } from "react";

/**
 * ScaleRuler Component Props
 */
interface ScaleRulerProps {
  minValue?: number;
  maxValue?: number;
  value?: number;
  step?: number;
  majorStep?: number;
  minorStep?: number;
  labelInterval?: number;
  width?: number;
  height?: number;
  tickSpacing?: number;
  activeColor?: string;
  inactiveColor?: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  labelColor?: string;
  triangleColor?: string;
  borderRadius?: number;
  formatValue?: (val: number) => string;
  formatLabel?: (val: number) => string;
  onChange?: (value: number) => void;
  displayLabel?: string;
  helperText?: string;
}

/**
 * ScaleRuler Component
 * A customizable ruler/slider component with tick marks and labels
 */
const ScaleRuler: React.FC<ScaleRulerProps> = ({
  // Value props
  minValue = 0,
  maxValue = 100,
  value = 50,
  step = 1,
  majorStep = 10,
  minorStep = 5,
  labelInterval = 10,

  // Styling props
  width = 350,
  height = 80,
  tickSpacing = 10,
  activeColor = "#7BD2EC",
  inactiveColor = "#DADCDF",
  backgroundColor = "white",
  borderColor = "#DADCDF",
  textColor = "#000",
  labelColor = "#586670",
  triangleColor = "#DBF0F9",
  borderRadius = 15,

  // Formatting props
  formatValue = (val: number) => val.toString(),
  formatLabel = (val: number) => val.toString(),

  // Event handlers
  onChange = (val: number) => {
    /* default empty handler */
  },

  // Display props
  displayLabel,
  helperText,
}) => {
  // Ensure value is within bounds
  const clampValue = (val: number): number => {
    return Math.min(Math.max(minValue, val), maxValue);
  };

  // Ensure value aligns with steps
  const alignToStep = (val: number): number => {
    const offset = val - minValue;
    const steps = Math.round(offset / step);
    return minValue + steps * step;
  };

  // Validate input value
  const validateValue = (val: number): number => {
    const clamped = clampValue(val);
    return alignToStep(clamped);
  };

  // Get initial value validated
  const initialValidValue = validateValue(value);

  // Component state
  const [displayValue, setDisplayValue] = useState<number>(initialValidValue);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Refs
  const rulerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef<boolean>(false);
  const isProgrammaticScrollRef = useRef<boolean>(false);
  const dragStartXRef = useRef<number>(0);
  const dragStartScrollRef = useRef<number>(0);

  // Calculate ruler dimensions
  const tickCount = Math.floor((maxValue - minValue) / step) + 1;
  const rulerWidth = tickCount * tickSpacing;
  const halfContainerWidth = width / 2;

  // Sync component when parent value changes
  useEffect(() => {
    if (isInitialized) {
      const validVal = validateValue(value);
      if (validVal !== displayValue) {
        setDisplayValue(validVal);
        centerToValue(validVal);
      }
    }
  }, [value, isInitialized]);

  // Initialize on first render
  useEffect(() => {
    if (!isInitialized && rulerRef.current) {
      // Use a timeout to ensure the component is fully rendered
      setTimeout(() => {
        centerToValue(initialValidValue);
        setIsInitialized(true);
      }, 100);
    }
  }, [isInitialized, initialValidValue]);

  // Convert value to scroll position
  const valueToScrollPosition = (val: number): number => {
    // Calculate the step index for this value
    const valueOffset = val - minValue;
    const stepIndex = valueOffset / step;

    // Calculate the pixel position of this step
    const pixelPosition = stepIndex * tickSpacing;

    // Calculate scroll position to center this pixel position
    const scrollPos = pixelPosition;

    // Ensure we don't exceed bounds
    const maxScroll = Math.max(0, rulerWidth - width);
    return Math.max(0, Math.min(scrollPos, maxScroll));
  };

  // Convert scroll position to value
  const scrollPositionToValue = (scrollPos: number): number => {
    // Calculate the exact value based on scroll position
    const effectivePosition = scrollPos;
    const stepCount = Math.round(effectivePosition / tickSpacing);
    const rawValue = minValue + stepCount * step;

    return validateValue(rawValue);
  };

  // const scrollPositionToValue = (scrollPos: number): number => {
  //   // Calculate the center position in the viewport
  //   const centerPosition = scrollPos + halfContainerWidth;

  //   // Find the nearest step
  //   const stepIndex = Math.round(centerPosition / tickSpacing);

  //   // Calculate the value
  //   const rawValue = minValue + (stepIndex * step);

  //   return validateValue(rawValue);
  // };

  // Center the ruler to a specific value
  const centerToValue = (val: number): void => {
    if (!rulerRef.current) return;

    isProgrammaticScrollRef.current = true;

    // Calculate exact scroll position needed for this value
    const scrollPos = valueToScrollPosition(val);
    rulerRef.current.scrollLeft = scrollPos;

    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 100);
  };

  // Handle scroll events
  const handleScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    if (isProgrammaticScrollRef.current) return;

    const scrollPos = e.currentTarget.scrollLeft;
    const newValue = scrollPositionToValue(scrollPos);

    // Always update the display value
    setDisplayValue(newValue);

    if (!isDraggingRef.current) {
      // Only trigger onChange when not actively dragging
      onChange(newValue);
    }
  };

  // Event handler setup
  useEffect(() => {
    if (!rulerRef.current) return;

    const ruler = rulerRef.current;

    // Handler functions
    const handleDragStart = (clientX: number) => {
      if (!rulerRef.current) return;

      isDraggingRef.current = true;
      dragStartXRef.current = clientX;
      dragStartScrollRef.current = rulerRef.current.scrollLeft;

      rulerRef.current.style.cursor = "grabbing";
    };

    const handleDrag = (clientX: number) => {
      if (!isDraggingRef.current || !rulerRef.current) return;

      // Calculate drag distance
      const dx = clientX - dragStartXRef.current;
      const newScrollLeft = dragStartScrollRef.current - dx;

      // Apply scroll position with bounds checking
      const maxScroll = Math.max(0, rulerWidth - width);
      const boundedScroll = Math.max(0, Math.min(newScrollLeft, maxScroll));
      rulerRef.current.scrollLeft = boundedScroll;

      // Always update the value, even if unchanged
      const newValue = scrollPositionToValue(boundedScroll);
      setDisplayValue(newValue);
    };

    const handleDragEnd = () => {
      if (!isDraggingRef.current) return;

      isDraggingRef.current = false;

      if (rulerRef.current) {
        rulerRef.current.style.cursor = "grab";
      }

      // Notify parent of value change
      onChange(displayValue);
    };

    // Mouse handlers
    const onMouseDown = (e: MouseEvent) => {
      handleDragStart(e.clientX);
      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      handleDrag(e.clientX);
      if (isDraggingRef.current) e.preventDefault();
    };

    const onMouseUp = () => {
      handleDragEnd();
    };

    // Touch handlers
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        handleDragStart(e.touches[0].clientX);
        e.preventDefault();
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 1 && isDraggingRef.current) {
        handleDrag(e.touches[0].clientX);
        e.preventDefault();
      }
    };

    const onTouchEnd = () => {
      handleDragEnd();
    };

    // Add event listeners
    ruler.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    ruler.addEventListener("touchstart", onTouchStart, { passive: false });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd);

    // Cleanup
    return () => {
      ruler.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);

      ruler.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [displayValue, onChange, step, rulerWidth, width]);

  // Check tick types
  const getTickType = (val: number): "major" | "minor" | "regular" => {
    if ((val - minValue) % majorStep === 0) return "major";
    if ((val - minValue) % minorStep === 0) return "major";
    return "regular";
  };

  // Get label for a tick
  const getTickLabel = (val: number): string => {
    if ((val - minValue) % labelInterval === 0) {
      return formatLabel(val);
    }
    return "";
  };

  // Get tick color based on value
  const getTickColor = (type: "major" | "minor" | "regular"): string => {
    return type === "major" || type === "minor" ? activeColor : activeColor;
  };

  // Generate tick marks
  const ticks = Array.from({ length: tickCount }, (_, i) => {
    const tickValue = minValue + i * step;
    const tickType = getTickType(tickValue);
    const label = getTickLabel(tickValue);
    return { value: tickValue, type: tickType, label };
  });

  // Get tick height based on type
  const getTickHeight = (type: "major" | "minor" | "regular"): number => {
    switch (type) {
      case "major":
        return 24;
      case "minor":
        return 18;
      default:
        return 10;
    }
  };

  return (
    <div className="w-full flex flex-col items-center">
      {displayLabel && (
        <div className="mb-4 w-full">
          <p className="text-center text-lg">{displayLabel}</p>
        </div>
      )}

      <div
        style={{
          position: "relative",
          width: width,
          height: height,
          backgroundColor: backgroundColor,
          borderRadius: borderRadius,
          overflow: "visible",
          border: `1px solid ${borderColor}`,
          boxSizing: "border-box",
          marginBottom: "10px",
        }}
      >
        {/* Triangle indicator */}
        <div
          style={{
            position: "absolute",
            width: 28,
            height: 14,
            left: "calc(50% - 14px)",
            bottom: -14,
            background: triangleColor,
            clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
            zIndex: 10,
          }}
        />

        {/* Current value display */}
        {/* <div
          style={{
            position: "absolute",
            left: -1,
            width: "100%",
            textAlign: "center",
            color: textColor,
            fontSize: 20,
            fontWeight: "bold",
            zIndex: 5,
          }}
        >
          {formatValue(displayValue)}
        </div> */}

        {/* Scrollable ruler */}
        <div
          ref={rulerRef}
          style={{
            width: "100%",
            height: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            display: "flex",
            alignItems: "flex-end",
            padding: "10px 0",
            boxSizing: "border-box",
            cursor: "grab",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            paddingTop: "40px",
            WebkitOverflowScrolling: "touch",
          }}
          onScroll={handleScroll}
        >
          {/* Hide scrollbar */}
          <style>{`::-webkit-scrollbar { display: none; }`}</style>

          {/* Tick marks container */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              width: rulerWidth,
              height: 40,
              position: "relative",
              marginLeft: halfContainerWidth,
              marginRight: halfContainerWidth,
            }}
          >
            {ticks.map((tick, index) => (
              <div
                key={index}
                style={{
                  position: "absolute",
                  left: `${index * tickSpacing}px`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  height: "100%",
                  justifyContent: "flex-end",
                }}
              >
                {tick.label && (
                  <span
                    style={{
                      color: labelColor,
                      fontSize: displayValue === tick.value ? 20 : 12,
                      position: "absolute",
                      top: displayValue === tick.value ? -20 : -10,
                      whiteSpace: "nowrap",
                      fontWeight:
                        displayValue === tick.value ? "bold" : "normal",
                      transition: "ease 0.2s",
                    }}
                  >
                    {tick.label}
                  </span>
                )}
                <div
                  style={{
                    // width: 2,
                    height: getTickHeight(tick.type),
                    backgroundColor: getTickColor(tick.type),
                    marginBottom: tick.type === "regular" ? 8 : 4,
                    border: "1px solid var(--colors-ABM-Blue80, #7BD2EC)",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {helperText && (
        <div className="mt-6 w-full">
          <p className="text-center text-sm text-gray-500">{helperText}</p>
        </div>
      )}
    </div>
  );
};

export default ScaleRuler;
